import { useMemo, useState } from 'react'
import {
  ResponsiveContainer,
  LineChart,
  Line,
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
  Cell,
} from 'recharts'
import { Info, LineChart as LineChartIcon } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { PageHeader } from '@/components/ui/page-header'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { TableSkeleton } from '@/components/ui/skeleton'
import { useSnapshots } from '@/hooks/useSnapshots'
import { useTransactions } from '@/hooks/useTransactions'
import { useAllBudgets } from '@/hooks/useBudgets'
import { useCategories } from '@/hooks/useCategories'
import { useRecurringRules } from '@/hooks/useRecurring'
import { useCurrentRates, convertWithRates } from '@/hooks/useCurrentRates'
import { monthOccurrenceStatus } from '@/lib/recurring'
import { fxOfMonth, posAt } from '@/lib/position'
import { endOfMonthISO, faturaMonthOf } from '@/lib/finance'
import { useAccounts } from '@/hooks/useAccounts'
import { Checkbox } from '@/components/ui/checkbox'
import { useHousehold } from '@/context/HouseholdContext'
import { formatCurrency } from '@/lib/currency'
import { useT } from '@/i18n/I18nProvider'

// Paleta categórica validada (CVD ΔE 73.6). Slots fixos: a cor segue a SÉRIE,
// nunca a posição — trocar o período não repinta as linhas.
const C = {
  contas: 'var(--viz-1)',
  itens: 'var(--viz-2)',
  total: 'var(--viz-3)',
}

function monthLabel(iso: string) {
  const [y, m] = iso.split('-')
  const nomes = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez']
  return `${nomes[Number(m) - 1]}/${y.slice(2)}`
}

export function ReportsPage() {
  const { baseCurrency } = useHousehold()
  const { t } = useT()
  const [tab, setTab] = useState<'orcamento' | 'patrimonio'>('orcamento')
  const [period, setPeriod] = useState('6')

  const PERIODS = [
    { v: '3', label: t('rep.periodMonths', { n: 3 }) },
    { v: '6', label: t('rep.periodMonths', { n: 6 }) },
    { v: '12', label: t('rep.periodMonths', { n: 12 }) },
    { v: '24', label: t('rep.periodMonths', { n: 24 }) },
    { v: '0', label: t('rep.periodAll') },
  ]

  const [showFuture, setShowFuture] = useState(true)
  const { data: snapshots } = useSnapshots()
  const { data: allBudgets } = useAllBudgets()
  const { data: categories } = useCategories()
  const { data: rules } = useRecurringRules()
  const { data: rates } = useCurrentRates()
  const { data: accounts } = useAccounts(true)
  // O orçamento é derivado das TRANSAÇÕES — não precisa de snapshot: receita e
  // despesa de um mês passado são reconstruíveis com exatidão a qualquer momento.
  const { data: allTxs } = useTransactions({})

  const nMonths = Number(period)

  const patrimonio = useMemo(() => {
    const rows = (snapshots ?? []).map((s) => ({
      month: s.month,
      label: monthLabel(s.month),
      contas: s.accounts_base + s.cards_base, // líquido de cartões (dívida abate)
      itens: s.items_base,
      total: s.total_base,
    }))
    return nMonths > 0 ? rows.slice(-nMonths) : rows
  }, [snapshots, nMonths])

  const orcamento = useMemo(() => {
    const hoje = new Date().toISOString().slice(0, 7)

    // Lançado por mês (transações). Régua de CAIXA/FATURA (faturaMonthOf), a MESMA
    // do Orçamento (BudgetPage usa faturaMonthOf) e do dashboard: uma compra de
    // cartão feita depois do fechamento cai no mês em que a FATURA vence, não na
    // data da compra. Agrupar por t.date jogava essas compras (ex.: Porto 17/07 →
    // fatura ago, base 121,59) no mês errado, e a sobra divergia do Orçamento.
    const lancado = new Map<string, { receita: number; despesa: number }>()
    const txsByMonth = new Map<string, typeof allTxs>()
    for (const t of allTxs ?? []) {
      const k = faturaMonthOf(t)
      txsByMonth.set(k, [...(txsByMonth.get(k) ?? []), t])
      if (t.transfer_group_id || t.amount_base == null) continue // transferência não é receita/despesa
      if (t.kind !== 'income' && t.kind !== 'expense') continue
      const cur = lancado.get(k) ?? { receita: 0, despesa: 0 }
      if (t.kind === 'income') cur.receita += Number(t.amount_base)
      else cur.despesa += Number(t.amount_base)
      lancado.set(k, cur)
    }

    // Orçado por mês (soma dos orçamentos das categorias, por tipo).
    const kindOf = new Map((categories ?? []).map((c) => [c.id, c.kind]))
    const orcado = new Map<string, { receita: number; despesa: number }>()
    for (const b of allBudgets ?? []) {
      const k = b.month.slice(0, 7)
      const cur = orcado.get(k) ?? { receita: 0, despesa: 0 }
      const kind = kindOf.get(b.category_id)
      if (kind === 'income') cur.receita += Number(b.amount_base)
      else if (kind === 'expense') cur.despesa += Number(b.amount_base)
      orcado.set(k, cur)
    }

    // Recorrentes do mês ainda NÃO lançados (a mesma projeção do Orçamento).
    function recorrentes(k: string) {
      const [y, m] = k.split('-').map(Number)
      const mTxs = txsByMonth.get(k) ?? []
      let receita = 0
      let despesa = 0
      for (const r of rules ?? []) {
        const kind = r.template.kind
        if (kind !== 'income' && kind !== 'expense') continue
        for (const o of monthOccurrenceStatus(r, mTxs, y, m - 1)) {
          if (o.included) continue
          const base = convertWithRates(r.template.amount, r.template.currency, rates)
          if (base == null) continue
          if (kind === 'income') receita += base
          else despesa += base
        }
      }
      return { receita, despesa }
    }

    const meses = [...new Set([...lancado.keys(), ...orcado.keys()])].sort()
    const rows = meses.map((k) => {
      const L = lancado.get(k) ?? { receita: 0, despesa: 0 }
      const R = recorrentes(k)
      const O = orcado.get(k) ?? { receita: 0, despesa: 0 }
      const futuro = k > hoje

      // PASSADO/CORRENTE: o que de fato aconteceu (+ recorrentes do mês que
      // ainda faltam lançar). FUTURO: usa o ORÇADO — senão a receita recorrente
      // entra inteira e as despesas não, inflando a sobra (bug já corrigido no
      // Orçamento). Sem orçamento no mês, cai no projetado.
      const receitaReal = L.receita + R.receita
      const despesaReal = L.despesa + R.despesa
      const receita = futuro && O.receita > 0.005 ? O.receita : receitaReal
      const despesa = futuro && O.despesa > 0.005 ? O.despesa : despesaReal

      // VARIAÇÃO CAMBIAL: o Orçamento a inclui no resultado do mês. Sem ela, as
      // duas telas dão números diferentes para o mesmo mês (era a divergência de
      // 35,33 em dez). Mesma função, um módulo só — não dá para esquecer de novo.
      const [yy, mm] = k.split('-').map(Number)
      const fx =
        accounts && rates && allTxs ? fxOfMonth(yy, mm - 1, allTxs, accounts, rates) : 0

      return {
        key: k,
        label: monthLabel(k + '-01'),
        receita,
        despesa,
        sobra: receita - despesa + fx,
        futuro,
      }
    })

    const passado = rows.filter((r) => !r.futuro)
    const janela = nMonths > 0 ? passado.slice(-nMonths) : passado
    // Projeção: mesma quantidade de meses à frente (ou 12, se "Tudo").
    const futuros = showFuture ? rows.filter((r) => r.futuro).slice(0, nMonths > 0 ? nMonths : 12) : []

    // SOBRA ACUMULADA = a POSIÇÃO REAL (contas + cartões) no fim de cada mês,
    // calculada AO VIVO com `posAt` — a MESMA função (e taxa de HOJE) que o
    // Orçamento e o dashboard usam. Antes vinha do SNAPSHOT congelado, que divergia
    // do Orçamento pela revalorização cambial (ex.: jun snapshot 16.361,73 vs
    // posição ao vivo ~16.483 → gap de 121,59 na linha). Usar posAt faz as duas
    // telas baterem centavo a centavo em meses fechados, corrente e futuros.
    //  • Mês FECHADO: posAt no fim do mês (posição real de hoje; sem pendências).
    //  • Mês CORRENTE e futuros: última posição fechada + soma das sobras projetadas
    //    (o corrente/futuro ainda tem recorrentes não lançados, que só entram via
    //    `sobra`, não via posAt).
    const canPos = Boolean(accounts && rates && allTxs)
    let acc = 0
    return [...janela, ...futuros].map((r) => {
      const fechado = r.key < hoje
      if (fechado && canPos) {
        const [yy, mm] = r.key.split('-').map(Number)
        acc = posAt(endOfMonthISO(yy, mm - 1), allTxs!, accounts!, rates!)
        return { ...r, acumulada: acc }
      }
      acc += r.sobra
      return { ...r, acumulada: acc }
    })
  }, [allTxs, allBudgets, categories, rules, rates, accounts, nMonths, showFuture])

  const fmt = (v: number) => formatCurrency(v, baseCurrency)
  const fmtAxis = (v: number) => `${Math.round(v / 1000)}k`

  // Tooltip próprio: o do recharts não formata moeda nem respeita o tema.
  interface TipItem {
    name?: string
    value?: number | null
    color?: string
  }
  function Tip({ active, payload, label }: { active?: boolean; payload?: TipItem[]; label?: string }) {
    if (!active || !payload?.length) return null
    return (
      <div className="bg-popover text-popover-foreground rounded-md border p-2 text-xs shadow-md">
        <div className="mb-1 font-medium">{label}</div>
        {payload.map((p, i) =>
          p.value == null ? null : (
            <div key={i} className="flex items-center justify-between gap-3">
              <span className="inline-flex items-center gap-1.5">
                <span className="size-2 rounded-full" style={{ background: p.color }} />
                {p.name}
              </span>
              <span className="tabular-nums">{fmt(p.value)}</span>
            </div>
          ),
        )}
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <PageHeader
        title={t('rep.title')}
        subtitle={t('rep.subtitle')}
        actions={
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[130px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PERIODS.map((p) => (
                <SelectItem key={p.v} value={p.v}>
                  {p.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
      />

      <Tabs value={tab} onValueChange={(v) => setTab(v as typeof tab)}>
        <TabsList className="grid w-full max-w-sm grid-cols-2">
          <TabsTrigger value="orcamento">{t('rep.tabBudget')}</TabsTrigger>
          <TabsTrigger value="patrimonio">{t('rep.tabNetworth')}</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* A ordem dos blocos abaixo é patrimônio-primeiro; a ordem das ABAS acima é
          orçamento-primeiro. A condição casa cada aba com o seu conteúdo. */}
      {tab === 'patrimonio' ? (
        patrimonio.length === 0 ? (
          <Card>
            <CardContent className="p-0">
              <EmptyState
                icon={LineChartIcon}
                title={t('rep.noDataTitle')}
                description={t('rep.networthEmptyDesc')}
              />
            </CardContent>
          </Card>
        ) : (
        <>
          <Card>
            <CardContent className="p-4">
              <div className="mb-3 text-sm font-semibold">{t('rep.networthChartTitle')}</div>
              <div className="h-[320px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={patrimonio} margin={{ top: 8, right: 12, left: 4, bottom: 0 }}>
                    <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" vertical={false} />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
                      tickLine={false}
                      axisLine={{ stroke: 'var(--border)' }}
                    />
                    <YAxis
                      tickFormatter={fmtAxis}
                      tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
                      tickLine={false}
                      axisLine={false}
                      width={44}
                    />
                    <Tooltip content={<Tip />} cursor={{ stroke: 'var(--border)' }} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <ReferenceLine y={0} stroke="var(--border)" />
                    <Line
                      type="monotone"
                      dataKey="contas"
                      name={t('rep.seriesAccountsCards')}
                      stroke={C.contas}
                      strokeWidth={2}
                      dot={{ r: 3 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="itens"
                      name={t('rep.seriesItems')}
                      stroke={C.itens}
                      strokeWidth={2}
                      dot={{ r: 3 }}
                      connectNulls={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="total"
                      name={t('rep.seriesTotal')}
                      stroke={C.total}
                      strokeWidth={2}
                      dot={{ r: 4 }}
                      connectNulls={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <div className="text-muted-foreground flex gap-2 rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 text-xs">
            <Info className="mt-0.5 size-4 shrink-0 text-amber-500" />
            <div>
              <b className="text-foreground">{t('rep.itemsNoteA')}</b>{' '}
              {t('rep.itemsNoteB')} <b>{t('rep.itemsNoteC')}</b> {t('rep.itemsNoteD')}{' '}
              <b>{t('rep.itemsNoteE')}</b>
              {t('rep.itemsNoteF')} <b>{t('rep.itemsNoteG')}</b> {t('rep.itemsNoteH')}
            </div>
          </div>

          {/* Tabela: exigida pela regra de relevo (uma das cores fica abaixo de 3:1
              no tema claro) e útil por si — dá o número exato de cada mês. */}
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('rep.thMonth')}</TableHead>
                    <TableHead className="text-right">{t('rep.thAccountsCards')}</TableHead>
                    <TableHead className="text-right">{t('rep.thItems')}</TableHead>
                    <TableHead className="text-right">{t('rep.thTotal')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {patrimonio.map((r) => (
                    <TableRow key={r.month}>
                      <TableCell className="font-medium">{r.label}</TableCell>
                      <TableCell className="text-right tabular-nums">{fmt(r.contas)}</TableCell>
                      <TableCell className="text-muted-foreground text-right tabular-nums">
                        {r.itens == null ? '—' : fmt(r.itens)}
                      </TableCell>
                      <TableCell className="text-right font-medium tabular-nums">
                        {r.total == null ? '—' : fmt(r.total)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
        )
      ) : !(accounts && rates && allTxs) ? (
        // Enquanto contas/taxas/transações não carregaram, a "Sobra acumulada"
        // (posAt) não pode ser calculada — mostrar a tabela agora renderizaria uma
        // soma corrida a partir do zero, que salta ao carregar. Skeleton até lá.
        <Card>
          <CardContent className="p-0">
            <TableSkeleton rows={6} cols={5} />
          </CardContent>
        </Card>
      ) : orcamento.length === 0 ? (
        <Card>
          <CardContent className="p-0">
            <EmptyState
              icon={LineChartIcon}
              title={t('rep.noDataTitle')}
              description={t('rep.budgetEmptyDesc')}
            />
          </CardContent>
        </Card>
      ) : (
        <>
          <Card>
            <CardContent className="p-4">
              <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                <span className="text-sm font-semibold">{t('rep.surplusMonthTitle')}</span>
                <label className="text-muted-foreground flex cursor-pointer items-center gap-2 text-xs">
                  <Checkbox checked={showFuture} onCheckedChange={(v) => setShowFuture(v === true)} />
                  {t('rep.projectFuture')}
                </label>
              </div>
              <p className="text-muted-foreground mb-3 text-xs">
                {t('rep.barDescA')} <b>{t('rep.barDescB')}</b>
                {t('rep.barDescC')} <b>{t('rep.barDescD')}</b> {t('rep.barDescE')}
              </p>
              <div className="h-[320px]">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={orcamento} margin={{ top: 8, right: 12, left: 4, bottom: 0 }}>
                    <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" vertical={false} />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
                      tickLine={false}
                      axisLine={{ stroke: 'var(--border)' }}
                    />
                    <YAxis
                      tickFormatter={fmtAxis}
                      tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
                      tickLine={false}
                      axisLine={false}
                      width={44}
                    />
                    <Tooltip content={<Tip />} cursor={{ fill: 'var(--accent)', opacity: 0.3 }} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <ReferenceLine y={0} stroke="var(--border)" />
                    <Bar dataKey="sobra" name={t('rep.barName')} legendType="none" radius={[4, 4, 0, 0]}>
                      {/* Cor = SINAL (sobrou/faltou), nunca identidade de série.
                          Preenchimento = CERTEZA: cheio é realizado, vazado é
                          projeção. Assim a distinção não depende só de cor. */}
                      {orcamento.map((r, i) => {
                        const cor = r.sobra >= 0 ? 'var(--viz-pos)' : 'var(--viz-neg)'
                        return (
                          <Cell
                            key={i}
                            fill={r.futuro ? 'transparent' : cor}
                            stroke={cor}
                            strokeWidth={r.futuro ? 2 : 0}
                            strokeDasharray={r.futuro ? '4 2' : undefined}
                          />
                        )
                      })}
                    </Bar>
                    {/* MESMO eixo Y das barras — de propósito. Eixo duplo é o erro
                        clássico: duas escalas fazem o leitor comparar alturas que
                        não são comparáveis. As duas séries estão na mesma ordem de
                        grandeza, então uma escala só é honesta e suficiente. */}
                    <Line
                      type="monotone"
                      dataKey="acumulada"
                      name={t('rep.lineAccumulated')}
                      stroke={C.total}
                      strokeWidth={2}
                      dot={{ r: 3 }}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('rep.thMonth')}</TableHead>
                    <TableHead className="text-right">{t('rep.thIncome')}</TableHead>
                    <TableHead className="text-right">{t('rep.thExpenses')}</TableHead>
                    <TableHead className="text-right">{t('rep.thSurplus')}</TableHead>
                    <TableHead className="text-right">{t('rep.thAccumulated')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orcamento.map((r) => (
                    <TableRow key={r.key} className={r.futuro ? 'opacity-70' : ''}>
                      <TableCell className="font-medium">
                        <span className="inline-flex items-center gap-1.5">
                          {r.label}
                          {r.futuro && (
                            <span className="text-muted-foreground border-muted-foreground/40 rounded border border-dashed px-1 text-[10px] font-normal">
                              {t('rep.projectionBadge')}
                            </span>
                          )}
                        </span>
                      </TableCell>
                      <TableCell className="text-success text-right tabular-nums">{fmt(r.receita)}</TableCell>
                      <TableCell className="text-destructive text-right tabular-nums">{fmt(r.despesa)}</TableCell>
                      <TableCell
                        className={`text-right font-medium tabular-nums ${r.sobra < 0 ? 'text-destructive' : 'text-success'}`}
                      >
                        {fmt(r.sobra)}
                      </TableCell>
                      <TableCell
                        className={`text-right font-medium tabular-nums ${r.acumulada < 0 ? 'text-destructive' : ''}`}
                      >
                        {fmt(r.acumulada)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  )
}
